import BaseHelper from 'helpers/base';
import {isNeedToSetZonesWb, getZoneKeyByNameAndAction} from 'store/modules/bidmanager/utils/area-utils';

import translation from 'translation';
import formatNumber from 'legacy/modules/common/components/MNumeric/formatNumber';

export const STRATEGY_TYPES = {
  MANUAL: 'MANUAL',
  AUTO: 'AUTO'
};

export const BIDDABLE_TYPES = {
  KEYWORD: {
    id: 'KEYWORD',
    name: () => translation.trc('instrumental', 'Keyword'),
    description: () => translation.trc('biddable_description', 'Changing bids and disabling keywords')
  },
  PLACEMENT_URL: {
    id: 'PLACEMENT_URL',
    name: () => translation.trc('instrumental', 'Placement url'),
    description: () => translation.trc('biddable_description', 'Disabling ineffective placement urls')
  },
  AD: {
    id: 'AD',
    name: () => translation.trc('instrumental', 'Ad'),
    description: () => translation.trc('biddable_description', 'Changing bids for ads')
  },
  ADGROUP: {
    id: 'ADGROUP',
    name: () => translation.trc('instrumental', 'Ad group'),
    description: () => translation.trc('biddable_description', 'Changing bids and disabling ad groups')
  },
  SEGMENTATION_RULE: {
    id: 'SEGMENTATION_RULE',
    name: () => translation.trc('instrumental', 'Segmentation rules'),
    description: () => null
  }
};


const generateAreaName = ({action, label, value = null, vat = false, areaName}) => {
  if (!action.zone_placements[label]) {
    return null;
  }

  if (!value && value !== 0) {
    return areaName;
  }

  const normalizedValue = formatNumber(value, 'currency', {vat});

  return `${areaName} (${normalizedValue})`;
};

export const nameGenerators = ({action, params, placementType, isManualStrategy = false}) => {
  const isNeedToSetZones =
    isNeedToSetZonesWb({placementType, channelId: params.channelId, isManualStrategy});

  return {
    bid_to_position () {
      if (!action.position || !action.max_bid || !action.increase_percent) {
        return '';
      }

      const position = BaseHelper.getById(params.positions, action.position).name;
      const percent = action.increase_percent;

      return translation.tr('Bid to position {0}, maximum bid {1}{2}, {3} by {4}').format(
        position,
        formatNumber(action.max_bid, 'currency', {vat: params.vat}),
        params.vat ? ' ({0})'.format(translation.tr('with VAT')) : '',
        percent >= 0 ? translation.tr('increasing') : translation.tr('decreasing'),
        formatNumber(Math.abs(percent), 'percent')
      );
    },

    bid_fixed_value () {
      const vat = params.vat;
      const vatText = vat ? ` (${translation.tr('with VAT')})` : '';

      if (isNeedToSetZones) {
        const mapLabelAndAreaData = {
          search: () => ({
            value: action[getZoneKeyByNameAndAction('search', action.type)],
            areaName: translation.trc('newBidmanager', 'Search')
          }),
          recommendations: () => ({
            value: action[getZoneKeyByNameAndAction('recommendations', action.type)],
            areaName: translation.trc('newBidmanager', 'Recommendations')
          })
        };

        const areas = Object.entries(mapLabelAndAreaData).
          map(([label, areaData]) =>
            generateAreaName({action, label, value: areaData().value, vat, areaName: areaData().areaName})).
          filter(Boolean).
          join(translation.tr(' and '));

        return translation.tr('Set fixed bid for areas {0}{1}').format(
          areas,
          vatText
        );
      }

      return translation.tr('Set fixed bid {0}{1}').format(
        formatNumber(action.bid, 'currency', {vat}),
        vatText
      );
    },

    increase_bid () {
      const type = action.relative_type;
      const style = type === 'percent' ? 'percent' : 'currency';
      const delta = action.delta;
      const vat = type === 'percent' ? false : params.vat;

      return translation.tr('Increase bid, {0} {1}{2}').format(
        translation.tr('add'),
        formatNumber(delta, style, {vat}),
        type === 'currency' && Boolean(vat) ? ' ({0})'.format(translation.tr('with VAT')) : ''
      );
    },


    decrease_bid () {
      const type = action.relative_type;
      const style = type === 'percent' ? 'percent' : 'currency';
      const delta = action.delta;
      const vat = type === 'percent' ? false : params.vat;

      return translation.tr('Decrease bid, {0} {1}{2}').format(
        translation.tr('subtract'),
        formatNumber(delta, style, {vat}),
        type === 'currency' && Boolean(vat) ? ' ({0})'.format(translation.tr('with VAT')) : ''
      );
    },


    bid_above_competitor () {
      const type = action.relative_type;
      const style = type === 'percent' ? 'percent' : 'currency';
      const delta = action.delta;
      const vat = type === 'percent' ? false : params.vat;

      return translation.tr('Bid above competitor, {0} {1}{2}').format(
        translation.tr('add'),
        formatNumber(delta, style, {vat}),
        type === 'currency' && Boolean(vat) ? ' ({0})'.format(translation.tr('with VAT')) : ''
      );
    },

    bid_below_competitor () {
      const type = action.relative_type;
      const style = type === 'percent' ? 'percent' : 'currency';
      const delta = action.delta;
      const vat = type === 'percent' ? false : params.vat;

      return translation.tr('Bid below competitor, {0} {1}{2}').format(
        translation.tr('subtract'),
        formatNumber(delta, style, {vat}),
        type === 'currency' && Boolean(vat) ? ' ({0})'.format(translation.tr('with VAT')) : ''
      );
    },

    pause_biddable () {
      const biddable = params.biddable.toLowerCase();
      return {
        adgroup: translation.tr('Pause ad group'),
        ad: translation.tr('Pause ad'),
        keyword: translation.tr('Pause keyword'),
        placement_url: translation.tr('Pause placement url')
      }[biddable] || '';
    },

    activate_biddable () {
      const biddable = params.biddable.toLowerCase();
      const biddableDataList = {
        adgroup: translation.tr('activate ad group'),
        ad: translation.tr('activate ad'),
        keyword: translation.tr('activate keyword'),
        placement_url: translation.tr('activate placement url')
      };

      return biddableDataList[biddable] || '';
    },

    remove_product () {
      return translation.tr('Remove SKU');
    },

    bid_position_for_search_cluster () {
      const vat = params.vat;
      const maxBid = formatNumber(action[isNeedToSetZones ? 'max_bid_search' : 'max_bid'], 'currency', {vat});

      return translation.tr('Show SKU on position from {0} to {1} for query "{2}", maximum bid {3}{4}').format(
        formatNumber(action.min_position, 'integer'), formatNumber(action.max_position, 'integer'), action.query,
        maxBid,
        vat ? ' ({0})'.format(translation.tr('with VAT')) : ''
      );
    },

    pause_placement () {
      return translation.tr('Stop placement');
    },

    bid_to_traffic_volume () {
      if (!action.traffic_volume || !action.max_bid || !action.increase_percent) {
        return '';
      }

      const percent = action.increase_percent;
      const maxBid = action.max_bid;
      const vat = params.vat;

      return translation.tr('Bid to traffic volume {0}, maximum bid {1}{2}, {3} by {4}').format(
        action.traffic_volume,
        formatNumber(maxBid, 'currency', {vat}),
        vat ? ' ({0})'.format(translation.tr('with VAT')) : '',
        percent >= 0 ? translation.tr('increasing') : translation.tr('decreasing'),
        formatNumber(Math.abs(percent), 'percent', {vat})
      );
    },

    set_min_bid () {
      if (isNeedToSetZones) {
        const mapLabelAndAreaName = {
          search: () => translation.trc('newBidmanager', 'Search'),
          recommendations: () => translation.trc('newBidmanager', 'Recommendations')
        };

        const areas = Object.entries(mapLabelAndAreaName).
          map(([label, areaName]) => generateAreaName({action, label, areaName: areaName()})).
          filter(Boolean).
          join(translation.tr(' and '));

        return translation.tr('Set minimum bid for areas {0}').format(areas);
      }

      return translation.tr('Set minimum bid');
    }
  };
};

export const STORE_TYPES = {

  // 1 BidManagerGroupListPage
  SET_CURRENT_BIDMANAGER_GROUP: 'SET_CURRENT_BIDMANAGER_GROUP',
  SET_CURRENT_BIDMANAGER_GROUP_ID: 'SET_CURRENT_BIDMANAGER_GROUP_ID',
  SET_PROGRESS_METRICS_LOADING: 'SET_PROGRESS_METRICS_LOADING',
  SET_PLACEMENTS: 'SET_PLACEMENTS',
  SET_GROUPS: 'SET_GROUPS',
  SET_GROUPS_TOTAL: 'SET_GROUPS_TOTAL',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_PROGRESS_DATA: 'SET_PROGRESS_DATA',
  SET_SORTING: 'SET_SORTING',
  SET_PER_PAGE: 'SET_PER_PAGE',

  FETCH_PLACEMENTS: 'FETCH_PLACEMENTS',
  FETCH_GROUPS: 'FETCH_GROUPS',
  FETCH_PROGRESS_METRICS_COLUMN: 'FETCH_PROGRESS_METRICS_COLUMN',
  DELETE_GROUP_REQUEST: 'DELETE_GROUP_REQUEST',
  TOGGLE_ENABLING: 'TOGGLE_ENABLING',
  FETCH_PROJECT_ANALYTICS: 'FETCH_PROJECT_ANALYTICS',

  // 2 Bidmanager
  FLUSH_STORE: 'FLUSH_STORE',
  FETCH_GROUP: 'FETCH_GROUP',
  SAVE_GROUP: 'SAVE_GROUP',
  FETCH_PARAMS: 'FETCH_PARAMS',
  CHANGE_CHANNEL: 'CHANGE_CHANNEL',
  INIT_GROUP: 'INIT_GROUP',
  COPY_FROM_GROUP: 'COPY_FROM_GROUP',
  ADD_RULE: 'ADD_RULE',
  ADD_DEFAULT_RULE: 'ADD_DEFAULT_RULE',
  SET_RULE: 'SET_RULE',
  MOVE_RULES: 'MOVE_RULES',
  RESET_RULES: 'RESET_RULES',
  RESET_GROUP_AFTER_PLACEMENT_CHANGE: 'RESET_GROUP_AFTER_PLACEMENT_CHANGE',
  UPDATE_GROUP: 'UPDATE_GROUP',
  UPDATE_GROUP_OPTIONS: 'UPDATE_GROUP_OPTIONS',
  DELETE_RULE: 'DELETE_RULE',
  COPY_RULE: 'COPY_RULE',
  SET_LABELS: 'SET_LABELS',
  SET_PLACEMENT_IDS: 'SET_PLACEMENT_IDS',
  SET_ACTION: 'SET_ACTION',
  ADD_CONDITION: 'ADD_CONDITION',
  SET_CONDITION: 'SET_CONDITION',
  SET_FALLBACK_ACTION: 'SET_FALLBACK_ACTION',
  TOGGLE_FALLBACK_ACTION_STATE: 'TOGGLE_FALLBACK_ACTION_STATE',
  REMOVE_CONDITION: 'REMOVE_CONDITION',
  SET_VALUE: 'SET_VALUE',
  SET_ENABLE_ON: 'SET_ENABLE_ON',
  SET_LOADING_CHANNEL_STATE: 'SET_LOADING_CHANNEL_STATE',
  TOGGLE_RULE: 'TOGGLE_RULE',
  SET_BIDDABLE_TYPE: 'SET_BIDDABLE_TYPE',
  SET_STRATEGY_TYPE: 'SET_STRATEGY_TYPE',
  SET_AUTOSTRATEGY_ID: 'SET_AUTOSTRATEGY_ID',
  SWITCH_AUTOSTRATEGY_VARIANT: 'SWITCH_AUTOSTRATEGY_VARIANT',
  RESET_AUTOSTRATEGY_ARGUMENTS: 'RESET_AUTOSTRATEGY_ARGUMENTS',
  SET_AUTOSTRATEGY_ARGUMENT: 'SET_AUTOSTRATEGY_ARGUMENT',
  SET_STRATEGIES_LIST: 'SET_STRATEGIES_LIST',
  FETCH_STRATEGIES_LIST: 'FETCH_STRATEGIES_LIST',
  RESET_AUTOSTRATEGY: 'RESET_AUTOSTRATEGY',
  CONVERT_AUTOSTRATEGY_TO_MANUAL_STRETEGY: 'CONVERT_AUTOSTRATEGY_TO_MANUAL_STRETEGY',
  RESET_STRATEGY: 'RESET_STRATEGY',

  IS_NEW: 'IS_NEW',
  IS_SELECTABLE_BIDDABLE_FOR_PLACEMENT: 'IS_SELECTABLE_BIDDABLE_FOR_PLACEMENT',
  SET_BIDDABLE_TYPES: 'SET_BIDDABLE_TYPES',
  IS_LOGGING_AVAILABLE: 'IS_LOGGING_AVAILABLE',
  SET_FETCHED_FIELDS: 'SET_FETCHED_FIELDS',
  SET_FETCHED_ACTIONS: 'SET_FETCHED_ACTIONS',
  SET_FETCHED_TYPES: 'SET_FETCHED_TYPES',
  SET_FETCHED_OPERATORS: 'SET_FETCHED_OPERATORS',
  SET_FETCHED_DATEFRAMES: 'SET_FETCHED_DATEFRAMES',
  SET_FETCHED_PLACEMENTS: 'SET_FETCHED_PLACEMENTS',
  SET_FETCHED_PROFILES: 'SET_FETCHED_PROFILES',
  SET_FETCHED_GOALS: 'SET_FETCHED_GOALS',
  SET_FETCHED_PROGRESS_METRICS: 'SET_FETCHED_PROGRESS_METRICS',
  SET_FETCHED_LABELS: 'SET_FETCHED_LABELS',
  SET_FETCHED_POSITIONS: 'SET_FETCHED_POSITIONS',
  SET_FETCHED_PARAMS: 'SET_FETCHED_PARAMS',
  SET_ERRORS: 'SET_ERRORS',
  SET_VALIDATION_RULES_ERRORS: 'SET_VALIDATION_RULES_ERRORS',
  FIX_SERVER_GROUP_STATE: 'FIX_SERVER_GROUP_STATE'

};

